import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';
import { CommandPalette } from './CommandPalette';
import type { NavItem } from './navItems';

interface SidebarSearchProps {
  readonly collapsed: boolean;
  readonly items: readonly NavItem[];
  readonly pathname: string;
}

/** True for ⌘K on a Mac and Ctrl+K elsewhere. */
function isPaletteShortcut(event: KeyboardEvent): boolean {
  return event.key.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey);
}

export const SidebarSearch: React.FC<SidebarSearchProps> = ({ collapsed, items, pathname }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const label = t('app.v11.shell.search.placeholder');

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (!isPaletteShortcut(event)) return;
      event.preventDefault();
      setOpen(prev => !prev);
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <>
      {collapsed ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={label}
          title={label}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-[rgba(11,11,11,0.1)] bg-v11-input-bg text-[rgba(11,11,11,0.5)] transition hover:opacity-80 dark:border-v11-border dark:text-white/50"
        >
          <Search className="h-3.5 w-3.5" strokeWidth={1.6} />
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex h-[35px] w-full items-center gap-2 rounded-full border border-[rgba(11,11,11,0.1)] bg-v11-input-bg px-3 text-[13px] leading-[normal] text-[rgba(11,11,11,0.5)] transition hover:text-v11-text-primary dark:border-v11-border dark:text-white/50"
        >
          <Search className="h-3 w-3 shrink-0" />
          <span className="flex-1 text-left">{label}</span>
          <kbd className="rounded-[6px] bg-[rgba(11,11,11,0.1)] px-1.5 py-0.5 font-sans text-[11px] font-medium leading-[normal] text-[rgba(11,11,11,0.5)] dark:bg-white/10 dark:text-white/50">
            ⌘K
          </kbd>
        </button>
      )}

      <CommandPalette open={open} onOpenChange={setOpen} items={items} pathname={pathname} />
    </>
  );
};
